import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import { useForumContext } from '../context/ForumContext';
import { usePageVisitTracking } from '../hooks/usePageVisitTracking';
import Container from '../components/common/Container';
import Button from '../components/common/Button';
import SectionHeader from '../components/common/SectionHeader';
import ForumQuestionList from '../components/features/forum/ForumQuestionList';
import ForumSearch from '../components/features/forum/ForumSearch';
import QuestionForm from '../components/features/forum/QuestionForm';
import ForumRules from '../components/features/forum/ForumRules';
import PageLayout from '../components/layout/PageLayout';
import EmailDebugPanel from '../components/debug/EmailDebugPanel';

import { ChatBubbleLeftRightIcon, PlusIcon } from '@heroicons/react/24/outline';

const ForumPage = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  
  // Track page visits for notifications
  usePageVisitTracking();
  
  // Get forum state from context
  const {
    questions,
    loading,
    filterState,
    updateSearchTerm,
    updateSortBy,
    updateTags,
    updateShowMyPostsOnly,
    addNewQuestion,
    loadQuestions,
    currentUserId
  } = useForumContext();
  
  // Local UI state
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  
  // Load questions on mount or when returning to forum
  useEffect(() => {
    if (user && currentUserId) {
      loadQuestions(currentUserId);
    }
  }, [user, currentUserId, loadQuestions]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Handle ask question
  const handleAskQuestion = () => {
    setShowQuestionForm(true);
  };

  // Handle question form close
  const handleQuestionFormClose = () => {
    setShowQuestionForm(false);
  };

  // Handle question created - add to list optimistically
  const handleQuestionCreated = (newQuestion) => {
    setShowQuestionForm(false);
    // Add new question to the list immediately for better UX
    addNewQuestion(newQuestion);
  };

  if (!user) {
    return (
      <div className="py-24 relative min-h-screen sm:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-100 via-pink-300 to-purple-500 opacity-10 -z-10"></div>
        <Container>
          <div className="mx-auto max-w-2xl text-center">
            <div className="h-12 mb-8 flex justify-center">
              <svg width="156" height="48" viewBox="0 0 156 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M25.067 12.161C25.067 13.172 24.5 14.075 23.659 14.6C24.5 15.124 25.067 16.027 25.067 17.04V18.617C25.067 20.378 23.637 21.806 21.881 21.806H14.987V7.398H21.881C23.637 7.398 25.067 8.825 25.067 10.588V12.161ZM21.881 16.456C21.881 15.576 21.168 14.863 20.286 14.863H18.173V17.972H20.286C21.168 17.972 21.881 17.335 21.881 16.456ZM21.881 12.736C21.881 11.855 21.168 11.143 20.286 11.143H18.173V14.252H20.286C21.168 14.252 21.881 13.615 21.881 12.736ZM31.061 21.817H27.943V7.395H31.061V21.817ZM36.112 7.398H46.221V11.143H39.298V14.025H45.454V16.995H39.298V17.989H46.221V21.806H36.112V7.398ZM59.227 21.923C58.046 21.923 56.951 21.605 56.017 21.044L54.829 22.231L52.516 19.915L53.704 18.729C53.143 17.793 52.823 16.701 52.823 15.517C52.823 14.334 53.143 13.242 53.704 12.306L52.516 11.12L54.829 8.804L56.017 9.991C56.951 9.43 58.046 9.112 59.227 9.112C62.642 9.112 65.397 11.868 65.397 15.281C65.397 18.695 62.642 21.923 59.227 21.923ZM59.227 12.858C57.782 12.858 56.598 14.04 56.598 15.487C56.598 16.934 57.782 18.114 59.227 18.114C60.672 18.114 61.856 16.934 61.856 15.487C61.856 14.040 60.672 12.858 59.227 12.858ZM76.142 7.458V18.304C76.142 19.597 75.09 20.647 73.8 20.647H71.459C70.168 20.647 69.117 19.597 69.117 18.304V7.458H66V18.304C66 21.348 68.417 23.766 71.459 23.766H73.8C76.843 23.766 79.26 21.348 79.26 18.304V7.458H76.142ZM82.856 21.817H86.005V7.395H82.856V21.817ZM93.636 11.231H89.831V7.458H97.429C98.72 7.458 99.772 8.507 99.772 9.772V17.608C99.772 19.478 98.309 21.008 96.502 21.201L94.935 21.367V19.648C91.952 19.648 93.636 21.817 93.636 21.817H90.518V13.812C90.518 12.374 91.785 11.231 93.636 11.231ZM96.624 17.573V11.231H93.753V17.573H96.624ZM101.756 21.817H105.085V7.395H101.756V21.817ZM109.928 7.398H120.037V11.143H113.114V14.025H119.27V16.995H113.114V17.989H120.037V21.806H109.928V7.398ZM129.045 7.398H132.164V17.632H137.448V21.806H129.045V7.398Z" fill="#3a0ca3"/>
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">Please log in</h1>
            <p className="mt-4 text-lg text-gray-600">You need to be logged in to view this page.</p>
            <Button as="a" href="/" className="mt-8">Return to Homepage</Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <PageLayout>
      <div className="py-4 sm:py-6 lg:py-8">

        
        {/* Header with Ask Question Button */}
        <Container variant="default" className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between">
            <SectionHeader 
              title="Community Forum"
              description="Connect with the Burnt community through meaningful discussions. Ask questions, share insights, and help fellow professionals grow their careers."
              icon={ChatBubbleLeftRightIcon}
              className="flex-1"
            />
            <Button
              onClick={handleAskQuestion}
              className="ml-4 sm:ml-6 flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white hidden md:flex"
              data-walkthrough-element="post-button"
            >
              <PlusIcon className="w-4 h-4" />
              <span>Post</span>
            </Button>
          </div>
        </Container>

        {/* Community Guidelines */}
        <Container variant="default" className="mb-4 sm:mb-6">
          <ForumRules />
        </Container>

        {/* Search and Filters */}
        <Container variant="default" className="mb-4 sm:mb-6">
          <ForumSearch
            searchTerm={filterState.searchTerm}
            onSearchChange={updateSearchTerm}
            sortBy={filterState.sortBy}
            onSortChange={updateSortBy}
            tags={filterState.tags}
            onTagsChange={updateTags}
            questions={questions}
            currentUserId={currentUserId}
            showMyPostsOnly={filterState.showMyPostsOnly}
            onMyPostsToggle={updateShowMyPostsOnly}
            resultCount={questions.length}
          />
        </Container>

        {/* Questions List */}
        <Container variant="tight">
          <ForumQuestionList />
        </Container>
      </div>
      
      {/* Mobile Floating Ask Question Button */}
      <button
        onClick={handleAskQuestion}
        className="fixed bottom-20 right-4 md:hidden bg-purple-600 hover:bg-purple-700 text-white rounded-full p-4 shadow-lg z-40 transition-all duration-200 hover:scale-110"
        aria-label="Post"
      >
        <PlusIcon className="w-6 h-6" />
      </button>

      {/* Question Form Modal */}
      <QuestionForm
        isOpen={showQuestionForm}
        onClose={handleQuestionFormClose}
        onQuestionCreated={handleQuestionCreated}
        mode="create"
      />

      {/* Email Debug Panel - only show in development */}
      {process.env.NODE_ENV === 'development' && <EmailDebugPanel />}
    </PageLayout>
  );
};

export default ForumPage;